import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '@environments/environment';

import { toFormData } from '@platform/shared/utilities/file-utility';

import { UrlComponentHttpParamEncoder } from '@platform/shared/http/url-component-http-param-encoder';
import { PostMobileUploadResponseDto } from '@core/mobile-upload/mobile-upload.model';
import { toPathString } from './document-utils';
import {
  CreateDocumentResultDto,
  CustomerSpaceUserLibraryPermissionDto,
  DeleteDocumentsDto,
  DocumentCommentDto,
  DocumentDto,
  DocumentLibraryAdminDto,
  DocumentLibraryDto,
  DocumentLibraryPermissionsOverviewDto,
  DocumentTemplateDto,
  LibraryTitlesDto,
  MoveCopyDocumentDto,
  PostDocumentCommentDto,
  PostDownloadDocumentsDto,
  RenameElementDto,
} from './document.model';

@Injectable({
  providedIn: 'root',
})
export class DocumentService {
  constructor(private http: HttpClient) {}

  public loadRecentlyUpdatedDocuments(customerSpaceId: string): Observable<DocumentDto[]> {
    return this.http.get<DocumentDto[]>(`${environment.apiUrl}/customer-spaces/${customerSpaceId}/documents/recently-updated`);
  }

  public loadDocumentLibraries(customerSpaceId: string): Observable<DocumentLibraryDto[]> {
    return this.http.get<DocumentLibraryDto[]>(`${environment.apiUrl}/customer-spaces/${customerSpaceId}/documents/libraries`);
  }

  public loadDocumentLibrariesPermissionsWithUsers(customerSpaceId: string) {
    return this.http.get<DocumentLibraryPermissionsOverviewDto>(
      `${environment.apiUrl}/customer-spaces/${customerSpaceId}/documents/librariesWithUsersOverview`,
    );
  }

  public loadDocumentLibrariesPermissions(customerSpaceId: string, libraryTitles: string[]) {
    const libraryTitlesDto: LibraryTitlesDto = { libraryTitles };

    return this.http.post<CustomerSpaceUserLibraryPermissionDto[]>(
      `${environment.apiUrl}/customer-spaces/${customerSpaceId}/documents/librariesPermissionsOverview`,
      libraryTitlesDto,
    );
  }

  public loadDocumentLibraryAdmin(customerSpaceId: string, libraryTitle: string): Observable<DocumentLibraryAdminDto> {
    const params = new HttpParams().set('libraryTitle', libraryTitle);

    return this.http.get<DocumentLibraryAdminDto>(`${environment.apiUrl}/customer-spaces/${customerSpaceId}/documents/libraries/admin`, {
      params,
    });
  }

  public createLibrary(customerSpaceId: string, libraryTitle: string): Observable<DocumentLibraryAdminDto> {
    const params = new HttpParams().set('libraryTitle', libraryTitle);

    return this.http.post<DocumentLibraryAdminDto>(`${environment.apiUrl}/customer-spaces/${customerSpaceId}/documents/libraries`, null, {
      params,
    });
  }

  public addUserToLibrary(customerSpaceId: string, libraryTitle: string, userId: string): Observable<DocumentLibraryAdminDto> {
    const params = new HttpParams().set('libraryTitle', libraryTitle).set('userId', userId);
    return this.http.post<DocumentLibraryAdminDto>(
      `${environment.apiUrl}/customer-spaces/${customerSpaceId}/documents/libraries/users`,
      null,
      {
        params,
      },
    );
  }

  public removeUserFromLibrary(customerSpaceId: string, libraryTitle: string, userId: string): Observable<DocumentLibraryAdminDto> {
    const params = new HttpParams().set('libraryTitle', libraryTitle).set('userId', userId);
    return this.http.delete<DocumentLibraryAdminDto>(`${environment.apiUrl}/customer-spaces/${customerSpaceId}/documents/libraries/users`, {
      params,
    });
  }

  public editLibrary(customerSpaceId: string, libraryId: string, libraryTitle: string): Observable<DocumentLibraryAdminDto> {
    const params = new HttpParams().set('libraryId', libraryId).set('libraryTitle', libraryTitle);

    return this.http.patch<DocumentLibraryAdminDto>(`${environment.apiUrl}/customer-spaces/${customerSpaceId}/documents/libraries`, null, {
      params,
    });
  }

  public deleteLibrary(customerSpaceId: string, libraryId: string): Observable<DocumentLibraryDto> {
    const params = new HttpParams().set('libraryId', libraryId);

    return this.http.delete<DocumentLibraryDto>(`${environment.apiUrl}/customer-spaces/${customerSpaceId}/documents/libraries`, {
      params,
    });
  }

  public loadFilesAndFolders(
    customerSpaceId: string,
    path: string[],
    libraryTitle: string,
    libraryName: string,
  ): Observable<DocumentDto[]> {
    const params = new HttpParams({ encoder: new UrlComponentHttpParamEncoder() })
      .set('path', toPathString(path))
      .set('libraryTitle', libraryTitle)
      .set('libraryName', libraryName);

    return this.http.get<DocumentDto[]>(`${environment.apiUrl}/customer-spaces/${customerSpaceId}/documents`, { params });
  }

  public uploadDocuments(
    customerSpaceId: string,
    path: string[],
    files: FileList,
    libraryTitle: string,
    libraryName: string,
    destPath: string[] | undefined = undefined,
  ): Observable<DocumentDto[]> {
    const formData = toFormData(files);
    const pathParam = destPath ? toPathString(destPath) : toPathString(path);
    const params = new HttpParams({ encoder: new UrlComponentHttpParamEncoder() })
      .set('path', pathParam)
      .set('libraryTitle', libraryTitle)
      .set('libraryName', libraryName);

    return this.http.post<DocumentDto[]>(`${environment.apiUrl}/customer-spaces/${customerSpaceId}/documents/upload`, formData, { params });
  }

  public createMobileUploadForPath(
    customerSpaceId: string,
    path: string[],
    libraryTitle: string,
    libraryName: string,
  ): Observable<PostMobileUploadResponseDto> {
    const params = new HttpParams({ encoder: new UrlComponentHttpParamEncoder() })
      .set('path', toPathString(path))
      .set('libraryTitle', libraryTitle)
      .set('libraryName', libraryName);

    return this.http.post<PostMobileUploadResponseDto>(
      `${environment.apiUrl}/customer-spaces/${customerSpaceId}/documents/mobile-upload`,
      {},
      { params },
    );
  }

  public createFolder(
    customerSpaceId: string,
    path: string[],
    folderName: string,
    libraryTitle: string,
    libraryName: string,
  ): Observable<DocumentDto[]> {
    const params = new HttpParams({ encoder: new UrlComponentHttpParamEncoder() })
      .set('path', toPathString(path))
      .set('folderName', folderName)
      .set('libraryTitle', libraryTitle)
      .set('libraryName', libraryName);

    return this.http.post<DocumentDto[]>(`${environment.apiUrl}/customer-spaces/${customerSpaceId}/documents/create-folder`, null, {
      params,
    });
  }

  public createDocument(
    customerSpaceId: string,
    path: string[],
    fileName: string,
    templateType: DocumentTemplateDto,
    libraryTitle: string,
    libraryName: string,
  ): Observable<CreateDocumentResultDto> {
    const params = new HttpParams({ encoder: new UrlComponentHttpParamEncoder() })
      .set('path', toPathString(path))
      .set('fileName', fileName)
      .set('templateType', templateType)
      .set('libraryTitle', libraryTitle)
      .set('libraryName', libraryName);
    return this.http.post<CreateDocumentResultDto>(
      `${environment.apiUrl}/customer-spaces/${customerSpaceId}/documents/create-document`,
      null,
      {
        params,
      },
    );
  }

  public renameElement(customerSpaceId: string, renameDto: RenameElementDto): Observable<DocumentDto[]> {
    return this.http.post<DocumentDto[]>(`${environment.apiUrl}/customer-spaces/${customerSpaceId}/documents/rename`, renameDto);
  }

  public deleteDocuments(customerSpaceId: string, deleteDocumentsDto: DeleteDocumentsDto): Observable<DocumentDto[]> {
    return this.http.post<DocumentDto[]>(`${environment.apiUrl}/customer-spaces/${customerSpaceId}/documents/delete`, deleteDocumentsDto);
  }

  public moveDocument(customerSpaceId: string, moveCopyDocumentDtos: MoveCopyDocumentDto[]): Observable<DocumentDto[]> {
    return this.http.post<DocumentDto[]>(`${environment.apiUrl}/customer-spaces/${customerSpaceId}/documents/move`, moveCopyDocumentDtos);
  }

  public copyDocument(customerSpaceId: string, moveCopyDocumentDtos: MoveCopyDocumentDto[]): Observable<DocumentDto[]> {
    return this.http.post<DocumentDto[]>(`${environment.apiUrl}/customer-spaces/${customerSpaceId}/documents/copy`, moveCopyDocumentDtos);
  }

  public loadDocumentComments(customerSpaceId: string, documentId: string): Observable<DocumentCommentDto[]> {
    return this.http.get<DocumentCommentDto[]>(`${environment.apiUrl}/customer-spaces/${customerSpaceId}/documents/${documentId}/comments`);
  }

  public createDocumentComment(
    customerSpaceId: string,
    documentId: string,
    comment: PostDocumentCommentDto,
  ): Observable<DocumentCommentDto[]> {
    return this.http.post<DocumentCommentDto[]>(
      `${environment.apiUrl}/customer-spaces/${customerSpaceId}/documents/${documentId}/comments`,
      comment,
    );
  }

  public deleteDocumentComment(customerSpaceId: string, documentId: string, commentId: string): Observable<DocumentCommentDto[]> {
    return this.http.delete<DocumentCommentDto[]>(
      `${environment.apiUrl}/customer-spaces/${customerSpaceId}/documents/${documentId}/comments/${commentId}`,
    );
  }

  public downloadFile(customerSpaceId: string, absoluteUrls: string[]): Observable<Blob> {
    const dto: PostDownloadDocumentsDto = { serverAbsoluteUrls: absoluteUrls };
    return this.http.post(`${environment.apiUrl}/customer-spaces/${customerSpaceId}/documents/download`, dto, { responseType: 'blob' });
  }
}
